// modeToggleNav.js — LOOP C / DEFECT (AI Agent toggle routing): the canonical routing decision
// for the Standard | AI Agent mode toggle in the side nav.
//
// The agent EXPERIENCE shell (AgentExperienceShell) is mounted by exactly ONE React Router route —
// `path: 'activity'` (routes/index.js). Its mode listener lives inside the shell, so the agent
// CONTENT only exists on /activity. The toggle handler is vanilla JS in _side_nav.html.haml, OUTSIDE
// React Router — so a history.replaceState there flips the nav chrome but never mounts the shell.
//
// ENTER bug (LOOP C, fixed in 783bee88): off /activity the handler computed '/activity?mode=agent'
// and DISCARDED it (only replaceState'd when already on /activity) → the route never changed → split
// agent-nav / standard-content state. Fix: from any non-/activity page, switching to agent does a
// REAL navigation.
//
// EXIT bug (this follow-up): clicking STANDARD while on /activity only flipped the chrome
// (replaceState to /activity) — the user stayed on /activity and the main pane still showed the agent
// board (split standard-nav / agent-content, the mirror image of the ENTER bug). Fix: EXIT from
// /activity does a REAL navigation OFF /activity, back to wherever the user entered agent mode from
// (the stored returnTo) or, failing that, the Standard dashboard ('/'). The user MUST leave /activity.
//
// Return-path bookkeeping (sessionStorage key `vesta:agentReturnTo`) is owned by the CALLER, not this
// pure function: on ENTER the function reports a `store` instruction ({ key, value }) for the caller
// to write; on EXIT the caller passes the previously-stored value in as `storedReturnTo` and the
// function picks the destination. This keeps the function pure (no DOM/storage access) while staying
// the single source of truth; the HAML inline handler mirrors it (incl. the sessionStorage read/write).
//
// Returns one of:
//   { action: 'replace', url, store }        — in-place URL flip via history.replaceState (on /activity)
//   { action: 'assign',  url, store }         — full navigation via window.location.assign (ENTER: mount shell)
//   { action: 'assign',  url, clearReturnTo } — full navigation OFF /activity (EXIT: returnTo or '/')
//   { action: 'none',    url: null }          — chrome-only flip, no URL change needed
export const AGENT_RETURN_KEY = "vesta:agentReturnTo"

// A path is an "/activity path" if its pathname portion is exactly /activity (query/hash ignored).
// Used both to avoid storing an /activity URL as the returnTo and to reject one on the way back out.
export function isActivityPath(path) {
  if (!path) return false
  return String(path).split("?")[0].split("#")[0] === "/activity"
}

// Item #7: the Task Library is a mode-aware agent surface too. Both url forms count — the flat
// /library and the nested /companies/:slug/library (the form the View-all hrefs actually emit).
export function isLibraryPath(path) {
  if (!path) return false
  const p = String(path).split("?")[0].split("#")[0]
  return p === "/library" || /^\/companies\/[^/]+\/library$/.test(p)
}

// Item #7 taste round (2026-07-28): a path that HOSTS the chat surface (AgentExperienceShell →
// AgentStudioChat). Both activity url forms count since the nested-route repair. A sidebar chat
// click anywhere else (library, artifacts) has no chat pane to dispatch into — the nav must
// NAVIGATE instead (libraryReturn.sidebarChatNavTarget).
export function isActivityHostPath(path) {
  if (!path) return false
  const p = String(path).split("?")[0].split("#")[0]
  return p === "/activity" || /^\/companies\/[^/]+\/activity$/.test(p)
}

// True when a search/query string carries mode=agent (an agent-chrome URL).
export function hasAgentModeQuery(search) {
  return /[?&]mode=agent(&|$)/.test(String(search || ""))
}

// Item #7 taste round (2026-07-28), finding (i): the turbolinks:before-visit handler in
// react_app.js normalizes the leaving page's history entry — and it used to normalize to the bare
// PATHNAME, stripping ?mode=agent. BROWSER back from the library then landed on plain /activity:
// the server rendered Standard chrome and the user's agent session (open chat included) was gone,
// while the in-app back button worked. The entry keeps its search string whenever it carries
// mode=agent; every other query string keeps the old pathname-only normalization (blast-radius
// fence — this handler runs on every turbolinks visit off a React page).
export function historyEntryUrl(pathname, search = "") {
  const p = String(pathname || "")
  return hasAgentModeQuery(search) ? `${p}${search}` : p
}

// Strip mode=agent from a URL, preserving any other query params.
function stripAgentMode(pathname, search) {
  const params = String(search || "").replace(/^\?/, "").split("&")
    .filter((kv) => kv && kv !== "mode=agent")
  return params.length ? `${pathname}?${params.join("&")}` : pathname
}

export function decideModeToggleNav({ targetMode, pathname, search = "", storedReturnTo = null }) {
  const onActivity = pathname === "/activity"

  if (targetMode === "agent") {
    // ENTER: remember the originating page so EXIT can return here — but never store an /activity
    // path OR any URL already carrying ?mode=agent (the library agent form): either would defeat
    // the point and could trap the user in agent chrome.
    const currentFull = pathname + (search || "")
    const store = isActivityPath(currentFull) || hasAgentModeQuery(search)
      ? null
      : { key: AGENT_RETURN_KEY, value: currentFull }

    if (onActivity) {
      // Already on the route that hosts the shell → instant in-place flip, no reload (D-E).
      return { action: "replace", url: "/activity?mode=agent", store }
    }
    // Off /activity and turning the agent ON → the shell isn't mounted here; navigate for real.
    return { action: "assign", url: "/activity?mode=agent", store }
  }

  // targetMode === "standard"
  if (onActivity) {
    // EXIT while on /activity → the user MUST leave /activity (a chrome-only flip strands them on the
    // agent board). Navigate back to the stored returnTo when it's a real non-/activity page, else home.
    const returnTo = storedReturnTo && !isActivityPath(storedReturnTo) ? storedReturnTo : "/"
    return { action: "assign", url: returnTo, clearReturnTo: true }
  }
  // Item #7 EXIT while on an agent-mode library URL → a chrome-only flip would leave ?mode=agent in
  // the URL (a refresh/back restores agent chrome — split state). Strip the mode param via a REAL
  // navigation so the server renders Standard chrome and the URL agrees; the user STAYS on the
  // library page they chose to browse (the library renders identically in Standard).
  if (isLibraryPath(pathname) && hasAgentModeQuery(search)) {
    return { action: "assign", url: stripAgentMode(pathname, search) }
  }
  // Off /activity, switching to Standard → just flip the chrome; no navigation required.
  return { action: "none", url: null }
}
